import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { adminOrgPath, createLicApi } from '../../OrgLicensing/licApi';
import {
  BEIGE,
  CopyButton,
  EmptyRow,
  HEADING,
  LoadingBox,
  MUTED,
  StatusChip,
  ViewHeader,
  tableContainerSx,
} from '../../OrgLicensing/OrgCommon';
import { expiryIsoFromDateInput, formatDate, isEmail, parseDomainList, toDateInput } from '../../OrgLicensing/orgFormat';
import { StatusChip as BaseStatusChip } from '../../../ui';

const STATUSES = ['Active', 'Suspended'];
const EDITIONS = ['Online', 'Offline'];

const seatsInUseMessage = (error, org) => {
  const used = error?.body?.seatsUsed ?? org?.seatsUsed;
  return typeof used === 'number'
    ? `${used} seat${used === 1 ? ' is' : 's are'} in use — release some first.`
    : 'Some seats are in use — release some first.';
};

// ------------------------------------------------------------------ create dialogs

const NewOrgDialog = ({ open, api, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', seatCount: 5, expiry: '', domains: '', adminEmail: '', edition: 'Online' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm({ name: '', seatCount: 5, expiry: '', domains: '', adminEmail: '', edition: 'Online' });
      setError('');
    }
  }, [open]);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const seats = Number.parseInt(form.seatCount, 10);
  const domains = parseDomainList(form.domains);
  const valid = form.name.trim() && seats >= 1 && isEmail(form.adminEmail);

  const handleCreate = async () => {
    setBusy(true);
    setError('');
    try {
      const body = {
        type: 'Org',
        name: form.name.trim(),
        seatCount: seats,
        allowedDomains: domains,
        adminEmail: form.adminEmail.trim().toLowerCase(),
        edition: form.edition,
      };
      const expiresAtUtc = expiryIsoFromDateInput(form.expiry);
      if (expiresAtUtc) body.expiresAtUtc = expiresAtUtc;
      const result = await api.licFetch(adminOrgPath(), { method: 'POST', body });
      onCreated(result.org, body);
    } catch (createError) {
      if (createError.status !== 401) setError(createError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>New organization</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Name" required autoFocus value={form.name} onChange={set('name')} />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Seats"
              type="number"
              required
              inputProps={{ min: 1 }}
              value={form.seatCount}
              onChange={set('seatCount')}
              sx={{ flex: '1 1 120px' }}
            />
            <TextField
              label="Expires (optional)"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.expiry}
              onChange={set('expiry')}
              helperText="Leave empty for no expiry."
              sx={{ flex: '1 1 180px' }}
            />
          </Box>
          <TextField
            label="Allowed domains"
            placeholder="acme.com, acme.co.uk"
            value={form.domains}
            onChange={set('domains')}
            helperText="Anyone with an email on these domains can sign in with the org code. Separate with commas."
          />
          {domains.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: -1 }}>
              {domains.map((domain) => (
                <Chip key={domain} size="small" label={`@${domain}`} sx={{ backgroundColor: BEIGE, color: HEADING }} />
              ))}
            </Box>
          )}
          <TextField
            label="IT admin email"
            type="email"
            required
            value={form.adminEmail}
            onChange={set('adminEmail')}
            helperText="Becomes an Admin of the org and is emailed the org code."
          />
          <TextField
            select
            label="Edition"
            value={form.edition}
            onChange={set('edition')}
            helperText="Decides which installers (Online or Offline) its IT admins can download and are emailed about."
          >
            {EDITIONS.map((edition) => (
              <MenuItem key={edition} value={edition}>
                {edition}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={busy || !valid}>
          {busy ? <CircularProgress size={20} color="inherit" /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const NewPersonalDialog = ({ open, api, onClose, onCreated }) => {
  const [email, setEmail] = useState('');
  const [expiry, setExpiry] = useState('');
  const [edition, setEdition] = useState('Online');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setEmail('');
      setExpiry('');
      setEdition('Online');
      setError('');
    }
  }, [open]);

  const handleCreate = async () => {
    setBusy(true);
    setError('');
    try {
      const body = { type: 'Personal', ownerEmail: email.trim().toLowerCase(), edition };
      const expiresAtUtc = expiryIsoFromDateInput(expiry);
      if (expiresAtUtc) body.expiresAtUtc = expiresAtUtc;
      const result = await api.licFetch(adminOrgPath(), { method: 'POST', body });
      onCreated(result.org, body);
    } catch (createError) {
      if (createError.status !== 401) setError(createError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>New personal license</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Email"
            type="email"
            required
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            helperText="This person signs in with this email and no org code."
          />
          <TextField
            label="Expires (optional)"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={expiry}
            onChange={(event) => setExpiry(event.target.value)}
            helperText="Leave empty for no expiry."
          />
          <TextField
            select
            label="Edition"
            value={edition}
            onChange={(event) => setEdition(event.target.value)}
            helperText="Decides which installers (Online or Offline) its IT admins can download and are emailed about."
          >
            {EDITIONS.map((edition) => (
              <MenuItem key={edition} value={edition}>
                {edition}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={busy || !isEmail(email)}>
          {busy ? <CircularProgress size={20} color="inherit" /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ------------------------------------------------------------------ main view

/** Vavetek admin: every org and personal HUBAROC license, with create, inline edit, and a link into each dashboard. */
const AdminOrgLicensingView = () => {
  const navigate = useNavigate();
  const api = useMemo(() => createLicApi({ superAdmin: true }), []);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // { id, seatCount, expiry, status, edition }
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState(''); // 'org' | 'personal' | ''
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setToast({ open: true, message, severity });

  const load = useCallback(async () => {
    setError('');
    try {
      const result = await api.licFetch(adminOrgPath());
      setOrgs(Array.isArray(result.orgs) ? result.orgs : []);
    } catch (loadError) {
      if (loadError.status !== 401) setError(loadError.message);
    }
  }, [api]);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const startEdit = (org) =>
    setEditing({
      id: org.id,
      seatCount: org.seatCount ?? '',
      expiry: toDateInput(org.expiresAtUtc),
      status: org.status || 'Active',
      edition: org.edition || 'Online',
    });

  const saveEdit = async (org) => {
    const body = {};
    const seats = Number.parseInt(editing.seatCount, 10);
    if (org.type !== 'Personal' && !Number.isNaN(seats) && seats !== org.seatCount) {
      if (seats < 1) {
        notify('Seats must be at least 1.', 'error');
        return;
      }
      body.seatCount = seats;
    }
    if (editing.status && editing.status !== (org.status || 'Active')) {
      body.status = editing.status;
    }
    if (editing.edition && editing.edition !== (org.edition || 'Online')) {
      body.edition = editing.edition;
    }
    const currentExpiry = toDateInput(org.expiresAtUtc);
    if (editing.expiry !== currentExpiry) {
      if (editing.expiry) {
        body.expiresAtUtc = expiryIsoFromDateInput(editing.expiry);
      } else {
        body.clearExpiry = true;
      }
    }

    if (Object.keys(body).length === 0) {
      setEditing(null);
      return;
    }

    setSaving(true);
    try {
      const result = await api.licFetch(adminOrgPath(org.id), { method: 'PUT', body });
      if (result.org) {
        setOrgs((current) => current.map((item) => (item.id === org.id ? { ...item, ...result.org } : item)));
      } else {
        await load();
      }
      setEditing(null);
      notify(`${org.name || org.ownerEmail || 'License'} updated.`);
    } catch (saveError) {
      if (saveError.status === 401) return;
      notify(saveError.code === 'SeatsInUse' ? seatsInUseMessage(saveError, org) : saveError.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreated = (org, body) => {
    setDialog('');
    if (org?.id) {
      setOrgs((current) => [org, ...current.filter((item) => item.id !== org.id)]);
    } else {
      load();
    }
    if (body.type === 'Org') {
      const code = org?.orgCode ? ` Org code ${org.orgCode}` : ' The org code';
      notify(`Created ${org?.name || body.name}.${code} was emailed to ${body.adminEmail}.`);
    } else {
      notify(`Created a personal license for ${org?.ownerEmail || body.ownerEmail}.`);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return orgs;
    return orgs.filter((org) =>
      [org.name, org.orgCode, org.ownerEmail, ...(Array.isArray(org.allowedDomains) ? org.allowedDomains : [])]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [orgs, search]);

  return (
    <Box sx={{ textAlign: 'left' }}>
      <ViewHeader title="Org licenses" onRefresh={handleRefresh} refreshing={refreshing}>
        <Button variant="contained" onClick={() => setDialog('org')}>
          New organization
        </Button>
        <Button variant="outlined" onClick={() => setDialog('personal')}>
          New personal license
        </Button>
      </ViewHeader>

      <TextField
        size="small"
        placeholder="Search name, org code, email or domain"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ mb: 2, width: '100%', maxWidth: 420 }}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <LoadingBox />
      ) : (
        <Box sx={tableContainerSx}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Edition</TableCell>
                <TableCell>Org code</TableCell>
                <TableCell>Seats used / total</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <EmptyRow colSpan={8}>{orgs.length === 0 ? 'No org licenses yet.' : 'Nothing matches your search.'}</EmptyRow>
              ) : (
                filtered.map((org) => {
                  const isEditing = editing?.id === org.id;
                  const isPersonal = org.type === 'Personal';
                  return (
                    <TableRow key={org.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: HEADING }}>
                          {org.name || org.ownerEmail || '—'}
                        </Typography>
                        {isPersonal && org.ownerEmail && org.name && (
                          <Typography variant="caption" sx={{ color: MUTED }}>
                            {org.ownerEmail}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={org.type || 'Org'} sx={{ backgroundColor: BEIGE, color: HEADING }} />
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            select
                            size="small"
                            value={editing.edition}
                            onChange={(event) => setEditing((current) => ({ ...current, edition: event.target.value }))}
                            inputProps={{ 'aria-label': 'Edition' }}
                            sx={{ width: 120 }}
                          >
                            {EDITIONS.map((edition) => (
                              <MenuItem key={edition} value={edition}>
                                {edition}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <BaseStatusChip label={org.edition || 'Online'} tone={org.edition === 'Offline' ? 'neutral' : 'info'} />
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {org.orgCode ? (
                          <>
                            <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {org.orgCode}
                            </Typography>
                            <CopyButton value={org.orgCode} label="Copy org code" />
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {isEditing && !isPersonal ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{org.seatsUsed ?? 0} /</span>
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 1, 'aria-label': 'Seats' }}
                              value={editing.seatCount}
                              onChange={(event) => setEditing((current) => ({ ...current, seatCount: event.target.value }))}
                              sx={{ width: 90 }}
                            />
                          </Box>
                        ) : (
                          `${org.seatsUsed ?? 0} / ${org.seatCount ?? '—'}`
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="date"
                            inputProps={{ 'aria-label': 'Expires' }}
                            value={editing.expiry}
                            onChange={(event) => setEditing((current) => ({ ...current, expiry: event.target.value }))}
                            helperText={editing.expiry ? '' : 'No expiry'}
                            sx={{ width: 170 }}
                          />
                        ) : org.expiresAtUtc ? (
                          formatDate(org.expiresAtUtc)
                        ) : (
                          'No expiry'
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing ? (
                          <TextField
                            select
                            size="small"
                            value={editing.status}
                            onChange={(event) => setEditing((current) => ({ ...current, status: event.target.value }))}
                            sx={{ width: 130 }}
                          >
                            {STATUSES.map((status) => (
                              <MenuItem key={status} value={status}>
                                {status}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <StatusChip org={org} />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {isEditing ? (
                          <>
                            <Button size="small" onClick={() => saveEdit(org)} disabled={saving} sx={{ textTransform: 'none' }}>
                              {saving ? <CircularProgress size={16} /> : 'Save'}
                            </Button>
                            {editing.expiry && (
                              <Button
                                size="small"
                                disabled={saving}
                                onClick={() => setEditing((current) => ({ ...current, expiry: '' }))}
                                sx={{ textTransform: 'none' }}
                              >
                                No expiry
                              </Button>
                            )}
                            <Button size="small" color="inherit" onClick={() => setEditing(null)} disabled={saving} sx={{ textTransform: 'none' }}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="small" onClick={() => startEdit(org)} disabled={Boolean(editing)} sx={{ textTransform: 'none' }}>
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => navigate(`/admin/org-licensing/${encodeURIComponent(org.id)}`)}
                            >
                              Open
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <NewOrgDialog open={dialog === 'org'} api={api} onClose={() => setDialog('')} onCreated={handleCreated} />
      <NewPersonalDialog open={dialog === 'personal'} api={api} onClose={() => setDialog('')} onCreated={handleCreated} />

      <Snackbar
        open={toast.open}
        autoHideDuration={8000}
        onClose={(event, reason) => reason !== 'clickaway' && setToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((current) => ({ ...current, open: false }))} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminOrgLicensingView;
